import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

const PasswordResetConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [sessionReady, setSessionReady] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let resolved = false;

    // Listen for PASSWORD_RECOVERY event — Supabase fires this when it processes
    // the #access_token=...&type=recovery hash fragment from the email link.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (resolved) return;
      if (event === 'PASSWORD_RECOVERY' && session) {
        resolved = true;
        setSessionReady(true);
        setChecking(false);
      }
    });

    // Also check if a recovery session is already active (e.g. page refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (resolved) return;
      if (session) {
        resolved = true;
        setSessionReady(true);
        setChecking(false);
      } else {
        // Give the onAuthStateChange listener a moment to fire for the hash fragment
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            setError('Reset link verlopen of ongeldig. Vraag een nieuwe reset link aan.');
            setChecking(false);
          }
        }, 1500);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push('Wachtwoord moet minimaal 8 tekens bevatten');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één hoofdletter bevatten');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één kleine letter bevatten');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één cijfer bevatten');
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidationErrors([]);

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        if (updateError.message.includes('expired') || updateError.message.includes('invalid')) {
          setError('Reset link verlopen of ongeldig. Vraag een nieuwe reset link aan.');
        } else {
          setError(updateError.message);
        }
        setLoading(false);
        return;
      }

      setSuccess(true);

      // Sign out and redirect to login after 2 seconds
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/user-authentication-login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Er is een onverwachte fout opgetreden');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Logo */}
      <header className="border-b border-gray-200">
        <div className="px-6 py-4">
          <img
            src="/assets/images/evoqia-logo_-nb-1766401973047.png"
            alt="Evoqia logo with geometric green and gray icon element and modern typography"
            className="h-10 object-contain"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#7CC363]/10 rounded-lg mb-4">
              <Lock className="h-6 w-6 text-[#7CC363]" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">Nieuw Wachtwoord Instellen</h1>
            <p className="text-gray-600">Voer uw nieuwe wachtwoord in</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 space-y-6">
            {checking ? (
              // Loading state while verifying the reset token
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7CC363]"></div>
                </div>
                <p className="text-sm text-gray-600">Reset link wordt geverifieerd...</p>
              </div>
            ) : success ? (
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Wachtwoord succesvol gewijzigd
                  </h3>
                  <p className="text-sm text-gray-600">
                    U wordt doorgestuurd naar de inlogpagina...
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p>{error}</p>
                        {error.includes('verlopen') && (
                          <button
                            type="button"
                            onClick={() => navigate('/password-reset-request')}
                            className="mt-2 text-red-700 underline font-medium"
                          >
                            Nieuwe reset link aanvragen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">Wachtwoord voldoet niet aan de eisen:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {validationErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Password Requirements Info */}
                {!error && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 font-medium mb-2">Wachtwoordeisen:</p>
                    <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                      <li>Minimaal 8 tekens</li>
                      <li>Minimaal één hoofdletter en één kleine letter</li>
                      <li>Minimaal één cijfer</li>
                    </ul>
                  </div>
                )}

                {/* Only show form fields when session is ready and no fatal error */}
                {sessionReady && (
                  <>
                    {/* New Password Input */}
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Nieuw Wachtwoord
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          id="newPassword"
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                          placeholder="••••••••"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          disabled={loading}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password Input */}
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Bevestig Nieuw Wachtwoord
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                          placeholder="••••••••"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          disabled={loading}
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7CC363] hover:bg-[#6AB352] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Wachtwoord wijzigen...
                        </>
                      ) : (
                        'Wijzig wachtwoord'
                      )}
                    </button>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetConfirmation;